import React from 'react';
import { DatePicker } from 'antd';
import get from 'lodash/get';
import dayjs from 'dayjs';

import { Select } from '../Select';
import { Input } from '../Input';
import type { DateRangeValue, FilterField } from './DataTable.types';
import { isFilterValueActive } from '../../filtering';

const { RangePicker } = DatePicker;

/** Generic copy the control needs — not domain text, so a plain object, not `useUIText()`. */
export interface FilterFieldControlTexts {
    all: string;
    startDate: string;
    endDate: string;
}

function parseDateRangeValue(value: unknown): [dayjs.Dayjs, dayjs.Dayjs] | null {
    if (!value || !Array.isArray(value) || value.length !== 2) return null;
    const [start, end] = value;
    if (!start || !end) return null;
    return [dayjs(start), dayjs(end)];
}

/**
 * EAA-1666: single source for "render a control for this filter field type" —
 * previously only lived inside `FilterFieldsList`. Now shared by that legacy
 * stacked list, the new per-column header `filterDropdown`, and the new
 * per-field toolbar popover, so the select/text/dateRange switch doesn't grow
 * a third independently-drifting copy.
 */
export function renderFilterFieldControl(
    field: FilterField,
    value: unknown,
    onChange: (value: unknown) => void,
    texts: FilterFieldControlTexts
): React.ReactNode {
    switch (field.type) {
        case 'select':
        case 'multiSelect':
            return (
                <Select
                    style={{ width: '100%' }}
                    placeholder={field.placeholder || texts.all}
                    // EAA-1817: a multi field's value is an array. antd wants
                    // `undefined`, not `null`, for "nothing selected" in
                    // `mode="multiple"` — `null` renders a stray blank tag.
                    mode={field.type === 'multiSelect' ? 'multiple' : undefined}
                    maxTagCount="responsive"
                    value={
                        field.type === 'multiSelect'
                            ? ((value as string[] | undefined) ?? [])
                            : value
                    }
                    onChange={onChange}
                    allowClear
                    options={field.options}
                    showSearch={get(field, 'showSearch', false)}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                        (option?.label?.toString() ?? '')
                            .toLowerCase()
                            .includes((input ?? '').toLowerCase())
                    }
                />
            );
        case 'text':
            return (
                <Input
                    placeholder={field.placeholder}
                    value={(value as string) || ''}
                    onChange={(e) => onChange(e.target.value)}
                    allowClear
                />
            );
        case 'dateRange':
            return (
                <RangePicker
                    style={{ width: '100%' }}
                    placeholder={[texts.startDate, texts.endDate]}
                    value={parseDateRangeValue(value)}
                    onChange={(dates: DateRangeValue) => {
                        if (!dates || !dates[0] || !dates[1]) {
                            onChange(null);
                        } else {
                            onChange([
                                dates[0].startOf('day').toISOString(),
                                dates[1].endOf('day').toISOString(),
                            ]);
                        }
                    }}
                    allowClear
                />
            );
        default:
            return null;
    }
}

/**
 * EAA-1666: "is this filter contributing to the active-filter count/highlight"
 * check, extracted from `TableToolbar`'s inline `activeFiltersCount` reducer
 * so the header filter icon and the new top-level "clear filters" action use
 * the exact same definition of "active" instead of a second copy.
 */
// EAA-1843: re-exported from the shared engine rather than defined twice. The
// two copies agreed when this was written, which is exactly how they stop
// agreeing later.
export { isFilterValueActive };

/**
 * EAA-1666: formats a filter's current value into the small inline chips the
 * per-column toolbar button shows (e.g. "Ứng dụng [lms][ach]"). `dateRange`
 * gets a single formatted range chip rather than two raw ISO-string chips.
 */
export function toFilterChips(field: FilterField, value: unknown): string[] {
    if (!isFilterValueActive(value)) return [];

    if (field.type === 'dateRange' && Array.isArray(value) && value.length === 2) {
        const [start, end] = value;
        if (!start || !end) return [];
        return [`${dayjs(start).format('DD/MM/YYYY')} - ${dayjs(end).format('DD/MM/YYYY')}`];
    }

    const values = Array.isArray(value) ? value : [value];
    return values
        .filter((item) => item !== null && item !== undefined && item !== '')
        .map((item) => field.options?.find((opt) => opt.value === item)?.label ?? String(item));
}
